import { useState, useEffect, useCallback, useRef } from 'react';
import { checkAvailability, type CheckoutItem } from '../lib/checkout';

export interface AvailabilityResult {
  sku: string;
  siteId: number;
  available: boolean;
  reason?: string;
}

interface UseAvailabilityCheckOptions {
  items: CheckoutItem[];
  cartSessionId?: string | null;
  enabled: boolean;
  intervalMs?: number;
}

export function useAvailabilityCheck({
  items,
  cartSessionId,
  enabled,
  intervalMs = 30_000,
}: UseAvailabilityCheckOptions) {
  const [results, setResults] = useState<Map<string, AvailabilityResult>>(new Map());
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const doCheck = useCallback(async () => {
    if (items.length === 0) {
      setResults(new Map());
      return true;
    }

    setChecking(true);
    setError(null);
    try {
      const data = await checkAvailability(items, cartSessionId);
      const map = new Map<string, AvailabilityResult>();
      for (const item of data) {
        map.set(`${item.sku}:${item.siteId}`, item);
      }
      setResults(map);
      setLastChecked(new Date());
      return data.every((r) => r.available);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Availability check failed');
      return true; // degrade gracefully — allow proceed if check fails
    } finally {
      setChecking(false);
    }
  }, [items, cartSessionId]);

  useEffect(() => {
    if (!enabled || items.length === 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    doCheck();
    intervalRef.current = setInterval(doCheck, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, doCheck, intervalMs, items.length]);

  const allAvailable = items.length === 0 || [...results.values()].every((r) => r.available);

  const unavailableItems = [...results.values()].filter((r) => !r.available);

  const getItemStatus = useCallback(
    (sku: string, siteId: number): AvailabilityResult | undefined => {
      return results.get(`${sku}:${siteId}`);
    },
    [results],
  );

  return {
    results,
    checking,
    lastChecked,
    error,
    allAvailable,
    unavailableItems,
    getItemStatus,
    recheckNow: doCheck,
  };
}
